import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    CheckBox,
    DateInput,
    Form,
    FormField,
    Select,
    TextArea,
    TextInput,
    Tip,
} from 'grommet';
import { ApiSearch } from '../../components/ApiSearch';
import { MatchWeightFields } from '../../components/MatchWeightFields';
import { FilterOptionSelect } from '../../components/FilterOptionSelect';
import { SearchSelect } from '../../components/SearchSelect';
import { Utils } from '../../helpers/Utils';
import AppConstants from '../../helpers/AppConstants';
import { getGradYears } from '../../helpers/GradYears';

export const GRAD_YEAR_OPTIONS = getGradYears().flatMap((year) => [
    `spring ${year}`,
    `fall ${year}`,
]);
export const DEGREE_TYPES = AppConstants.jobDegreeTypes;
export const WEIGHT_OPTIONS = AppConstants.weightOptions;

export const JobForm = ({
    companies,
    duplicateJob,
    isClientView = false,
    onSubmit,
}) => {
    const [buttonBusy, setButtonBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [value, setValue] = useState({
        additionalDetails: duplicateJob?.additionalDetails || '',
        applicationDeadline: duplicateJob?.applicationDeadline || null,
        applicationLink: duplicateJob?.applicationLink || '',
        cardDescription: duplicateJob?.cardDescription || '',
        ...(isClientView ? {} : { company: duplicateJob?.company?.id }),
        coverLetterRequired: duplicateJob?.coverLetterRequired || false,
        daysToApply: duplicateJob?.daysToApply || 7,
        daysToShowInterest: duplicateJob?.daysToShowInterest || 7,
        eligibleDegreeTypes: duplicateJob?.eligibleDegreeTypes || null,
        eligibleGradYears: duplicateJob?.eligibleGradYears || null,
        idealMajors: duplicateJob?.idealMajors || [],
        ignoreInterestDeadline: duplicateJob?.ignoreInterestDeadline || false,
        industries: duplicateJob?.industries || null,
        interviewProcess: duplicateJob?.interviewProcess || '',
        isRemote: duplicateJob?.isRemote || false,
        jobFunctions: duplicateJob?.jobFunctions || null,
        location: duplicateJob?.location || '',
        locations: duplicateJob?.locations || [],
        minimumGPA: duplicateJob?.minimumGPA || 3.0,
        responsibilities: duplicateJob?.responsibilities || '',
        resumeReadyWeight: duplicateJob?.resumeReadyWeight ?? 0,
        relevantTechnicalSkills: duplicateJob?.relevantTechnicalSkills || null,
        salaryAndBenefits: duplicateJob?.salaryAndBenefits || '',
        seats: duplicateJob?.seats || 0,
        title: duplicateJob?.title || '',
        welcomeCallCompletedWeight:
            duplicateJob?.welcomeCallCompletedWeight ?? 0,
        wouldSponsorVisa: duplicateJob?.wouldSponsorVisa || false,
    });

    const handleSubmit = async (jobData) => {
        setButtonBusy(true);

        if (jobData.locations.length == 0) {
            setErrorMessage(
                'Job location(s) not selected! Please select one or more location(s)',
            );

            setButtonBusy(false);
            return;
        }

        try {
            await onSubmit(jobData);
        } catch (error) {
            setErrorMessage(error?.message ?? 'An error occured.');
        }
        setButtonBusy(false);
    };

    return (
        <Form
            value={value}
            onChange={(nextValue) => setValue(nextValue)}
            onReset={() => setValue({})}
            onSubmit={({ value }) => {
                handleSubmit(value);
            }}>
            <Box gap="medium" width="large">
                {!isClientView && (
                    <FormField label="Company" name="company" required>
                        <SearchSelect
                            name="company"
                            labelKey="name"
                            valueKey="id"
                            defaultValue={value.company}
                            options={companies}
                            placeholder="Select Company"
                            updateFormData={(val) =>
                                setValue({ ...value, company: val })
                            }
                        />
                    </FormField>
                )}

                <FormField label="Job Title" name="title" required>
                    <TextInput name="title" />
                </FormField>

                {!isClientView && (
                    <FormField label="Status" name="status">
                        <Tip content='The "future job" status will prevent students from applying.'>
                            <Select
                                name="status"
                                options={AppConstants.jobStatusFilterOptions}
                                labelKey="label"
                                valueKey={{
                                    key: 'value',
                                    reduce: true,
                                }}
                                defaultValue={'open'}
                                margin={{
                                    vertical: 'small',
                                }}
                            />
                        </Tip>
                    </FormField>
                )}

                <FormField
                    label="Application Link"
                    name="applicationLink"
                    validate={(fieldValue) => {
                        const isValidUrl = Utils.validateUrl(fieldValue);
                        if (fieldValue && !isValidUrl)
                            return {
                                message: 'Invalid url',
                                status: 'error',
                            };
                    }}>
                    <TextInput
                        type="url"
                        name="applicationLink"
                        placeholder="Input must be a valid url or be left blank"
                    />
                </FormField>

                <FormField label="Display Location" name="location">
                    <TextInput name="location" required />
                </FormField>

                <FormField label="Location(s)" name="locations">
                    <ApiSearch
                        contentType="locations"
                        selectMultiple={true}
                        name="locations"
                        defaultValue={duplicateJob?.locations ?? null}
                        updateFormData={(val) => {
                            setValue({
                                ...value,
                                locations: val,
                            });
                        }}
                    />
                </FormField>

                <FormField label="Preview Description" name="cardDescription">
                    <TextArea name="cardDescription" />
                </FormField>

                <FormField label="Responsibilities" name="responsibilities">
                    <TextArea name="responsibilities" />
                </FormField>

                <FormField label="Interview Process" name="interviewProcess">
                    <TextArea name="interviewProcess" />
                </FormField>

                <FormField label="Salary & Benefits" name="salaryAndBenefits">
                    <TextArea name="salaryAndBenefits" />
                </FormField>

                <FormField label="Additional Details" name="additionalDetails">
                    <TextArea name="additionalDetails" />
                </FormField>

                <FormField
                    label="Eligible Graduation Years"
                    name="eligibleGradYears">
                    <FilterOptionSelect
                        placeholder="Graduation Years"
                        options={GRAD_YEAR_OPTIONS}
                        defaultValue={duplicateJob?.eligibleGradYears ?? null}
                        updateFormData={(val) => {
                            setValue({
                                ...value,
                                eligibleGradYears: val,
                            });
                        }}
                    />
                </FormField>

                <FormField
                    label="Eligible Degree Types"
                    name="eligibleDegreeTypes">
                    <FilterOptionSelect
                        placeholder="Degree Types"
                        options={DEGREE_TYPES}
                        defaultValue={duplicateJob?.eligibleDegreeTypes ?? null}
                        updateFormData={(val) => {
                            setValue({
                                ...value,
                                eligibleDegreeTypes: val,
                            });
                        }}
                    />
                </FormField>

                <FormField label="Minimum GPA Required" name="minimumGPA">
                    <TextInput type="number" name="minimumGPA" />
                </FormField>

                <FormField label="Ideal Majors" name="idealMajors">
                    <ApiSearch
                        contentType="majors"
                        selectMultiple={true}
                        name="idealMajors"
                        defaultValue={duplicateJob?.idealMajors ?? null}
                        updateFormData={(val) => {
                            setValue({
                                ...value,
                                idealMajors: val,
                            });
                        }}
                    />
                </FormField>

                <FormField
                    label="Technical Skills"
                    name="relevantTechnicalSkills">
                    <ApiSearch
                        contentType="technical-skills"
                        selectMultiple={true}
                        name="relevantTechnicalSkills"
                        defaultValue={
                            duplicateJob?.relevantTechnicalSkills ?? null
                        }
                        updateFormData={(val) => {
                            setValue({
                                ...value,
                                relevantTechnicalSkills: val,
                            });
                        }}
                    />
                </FormField>

                <FormField label="Industries" name="industries">
                    <ApiSearch
                        contentType="industries"
                        selectMultiple={true}
                        name="industries"
                        defaultValue={duplicateJob?.industries ?? null}
                        updateFormData={(val) => {
                            setValue({
                                ...value,
                                industries: val,
                            });
                        }}
                    />
                </FormField>

                <FormField label="Job Function(s)" name="jobFunctions">
                    <ApiSearch
                        contentType="job-functions"
                        selectMultiple={true}
                        name="jobFunctions"
                        defaultValue={duplicateJob?.jobFunctions ?? null}
                        updateFormData={(val) => {
                            setValue({
                                ...value,
                                jobFunctions: val,
                            });
                        }}
                    />
                </FormField>

                <Box direction="row" gap="small">
                    <FormField
                        label="Application Deadline"
                        name="applicationDeadline"
                        width="small"
                        info="only set if there is a hard application deadline">
                        <DateInput
                            name="applicationDeadline"
                            format="mm/dd/yyyy"
                            onChange={(e) => {
                                setValue({
                                    ...value,
                                    applicationDeadline: e.value,
                                });
                            }}
                            buttonProps={{
                                color: 'black',
                                margin: 'small',
                            }}
                            calendarProps={{
                                daysOfWeek: true,
                                size: 'medium',
                            }}
                            dropProps={{
                                background: 'enrich-grey',
                                color: 'enrich-green',
                                pad: 'small',
                            }}
                        />
                    </FormField>
                    <FormField label="Total seats" name="seats">
                        <TextInput type="number" name="seats" />
                    </FormField>
                </Box>

                <Box direction="row" gap="small">
                    <FormField
                        label="Days to show interest"
                        name="daysToShowInterest">
                        <TextInput name="daysToShowInterest" />
                    </FormField>
                    <FormField label="Days to apply" name="daysToApply">
                        <TextInput name="daysToApply" />
                    </FormField>
                </Box>

                <FormField name="coverLetterRequired">
                    <CheckBox
                        name="coverLetterRequired"
                        checked={value.coverLetterRequired}
                        label="Cover letter required"
                        onChange={(event) =>
                            setValue({
                                ...value,
                                coverLetterRequired: event.target.checked,
                            })
                        }
                    />
                </FormField>

                <FormField name="ignoreInterestDeadline">
                    <CheckBox
                        name="ignoreInterestDeadline"
                        checked={value.ignoreInterestDeadline}
                        label="Ignore interest deadline"
                        onChange={(event) =>
                            setValue({
                                ...value,
                                ignoreInterestDeadline: event.target.checked,
                            })
                        }
                    />
                </FormField>

                <FormField
                    name="isRemote"
                    style={{
                        border: { color: 'transparent' },
                    }}>
                    <CheckBox
                        name="isRemote"
                        checked={value.isRemote}
                        label="This is a remote position"
                        onChange={(event) =>
                            setValue({
                                ...value,
                                isRemote: event.target.checked,
                            })
                        }
                    />
                </FormField>

                <FormField
                    name="wouldSponsorVisa"
                    style={{
                        border: { color: 'transparent' },
                    }}>
                    <CheckBox
                        name="wouldSponsorVisa"
                        checked={value.wouldSponsorVisa}
                        label="Would sponsor a visa"
                        onChange={(event) =>
                            setValue({
                                ...value,
                                wouldSponsorVisa: event.target.checked,
                            })
                        }
                    />
                </FormField>

                <MatchWeightFields value={value} setValue={setValue} />

                <FormField error={errorMessage}>
                    <Box>
                        <Button
                            type="submit"
                            primary
                            label="Submit"
                            busy={buttonBusy}
                        />
                    </Box>
                </FormField>
            </Box>
        </Form>
    );
};

const jobShape = PropTypes.shape({
    additionalDetails: PropTypes.string,
    applicationDeadline: PropTypes.string,
    applicationLink: PropTypes.string,
    cardDescription: PropTypes.string,
    company: PropTypes.shape({ id: PropTypes.number }),
    coverLetterRequired: PropTypes.bool,
    daysToApply: PropTypes.number,
    daysToShowInterest: PropTypes.number,
    eligibleDegreeTypes: PropTypes.array,
    eligibleGradYears: PropTypes.array,
    idealMajors: PropTypes.array,
    ignoreInterestDeadline: PropTypes.bool,
    industries: PropTypes.array,
    interviewProcess: PropTypes.string,
    isRemote: PropTypes.bool,
    jobFunctions: PropTypes.array,
    location: PropTypes.string,
    locations: PropTypes.array,
    minimumGPA: PropTypes.number,
    responsibilities: PropTypes.string,
    resumeReadyWeight: PropTypes.number,
    relevantTechnicalSkills: PropTypes.array,
    salaryAndBenefits: PropTypes.string,
    seats: PropTypes.number,
    title: PropTypes.string,
    welcomeCallCompletedWeight: PropTypes.number,
    wouldSponsorVisa: PropTypes.bool,
});

JobForm.propTypes = {
    companies: PropTypes.array,
    duplicateJob: jobShape,
    isClientView: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
};
